import { CommonModule } from '@angular/common';
import { Component, inject, input, Input, OnDestroy, output, ViewChild } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { FeedbackService } from '@core/services/feedback.service';
import { LanguageService } from '@core/services/language.service';
import { UserService } from '@core/services/user.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroUsers } from '@ng-icons/heroicons/outline';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonPrimaryComponent } from '@shared/components/button-primary/button-primary.component';
import { NotificationsMenuComponent } from '@shared/components/notifications-menu/notifications-menu.component';
import { SubscriptionBadgeComponent } from '@shared/components/subscription-badge/subscription-badge.component';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { ILinkButton } from '@shared/interfaces/link-buttoninterface';
import { SubscriptionDetail, UserInfo } from '@shared/models/user-info';
import { isTouchDevice } from '@shared/utils/device.utils';
import { Subject, takeUntil } from 'rxjs';
import { HEADER_LANGUAGES, HEADER_USER_MENU, IHeaderLangItem } from './header.helper';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    ClickOutsideDirective,
    FontAwesomeModule,
    MatIcon,
    ButtonPrimaryComponent,
    NotificationsMenuComponent,
    SubscriptionBadgeComponent,
    NgIcon,
  ],
  providers: [provideIcons({ heroUsers })],
})
export class HeaderComponent implements OnDestroy {
  @Input() public navMenuOpen: boolean = false;
  @ViewChild(NotificationsMenuComponent) private notificationsMenu?: NotificationsMenuComponent;

  public languagesList: IHeaderLangItem[] = HEADER_LANGUAGES;
  public userMenuItems: ILinkButton[] = HEADER_USER_MENU;
  readonly headerClicked = output<void>();
  language: string = 'en';
  languageDropdownActive = false;
  userMenuActive = false;
  user!: UserInfo;
  subscriptionEnabled = false;
  faArrowLeft = faArrowLeft;
  isSecondary = false;

  supportLink: string = 'https://getbueno.com/help/';

  activeSubscription?: SubscriptionDetail;
  private auth = inject(AuthService);
  private languageService = inject(LanguageService);
  private userService = inject(UserService);
  private feedbackService = inject(FeedbackService);
  private destroy$ = new Subject<void>();

  readonly disableOptions = input<boolean>(false);

  constructor() {
    this.languageService
      .getLanguage()
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang: string) => {
        this.language = lang;
      });
  }

  ngOnInit(): void {
    this.auth.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.userService
          .getUserInfo()
          .pipe(takeUntil(this.destroy$))
          .subscribe(u => {
            this.user = u;
            const activeSubscription = this.user?.subscriptionsHistory?.find(x => x.isActive);
            this.activeSubscription = activeSubscription;
            this.subscriptionEnabled =
              !!activeSubscription?.paidUntil && new Date(activeSubscription.paidUntil) > new Date();
            this.isSecondary = localStorage.getItem('login_type') === 'secondary';
          });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setLanguage(lang: string) {
    this.language = lang.toLowerCase();
    this.languageService.setLanguage(lang.toLowerCase());
    // Close dropdown after language selection
    this.closeAllDropdowns();
  }

  onMenuClicked() {
    this.headerClicked.emit();
  }

  onLogout() {
    this.auth.logout();
  }

  toggleNavMenu() {
    this.navMenuOpen = !this.navMenuOpen;
  }

  launchCustomerPortal(): void {
    if (!this.activeSubscription?.stripeCustomerId) {
      return;
    }

    this.userService
      .stripeCustomerPortal(this.activeSubscription.stripeCustomerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        setTimeout(() => {
          window.open(session.url, '_blank');
        });
      });
  }

  toggleMenuDropdown(type: 'language' | 'user', event: Event, isOpen: boolean): void {
    const touchDevice = isTouchDevice();
    const eventType = event.type;

    // On touch devices, only respond to click events, ignore ALL hover events
    if (touchDevice && (eventType === 'mouseenter' || eventType === 'mouseleave')) {
      return;
    }

    // For click events, prevent event bubbling to avoid triggering click-outside
    if (eventType === 'click') {
      event.stopPropagation();
    }

    switch (eventType) {
      case 'click': {
        const wasActive = this.getMenuState(type);
        this.closeAllDropdowns();
        if (!wasActive) {
          this.setMenuState(type, true);
        }
        return;
      }
      case 'mouseenter':
        this.closeAllDropdowns();
        this.setMenuState(type, isOpen);
        return;
      case 'mouseleave':
        this.setMenuState(type, false);
        return;
      default:
        return;
    }
  }

  private getMenuState(type: 'language' | 'user'): boolean {
    return type === 'language' ? this.languageDropdownActive : this.userMenuActive;
  }

  private setMenuState(type: 'language' | 'user', isActive: boolean): void {
    if (type === 'language') {
      this.languageDropdownActive = isActive;
      return;
    }

    this.userMenuActive = isActive;
  }

  closeHeaderDropdowns(): void {
    this.languageDropdownActive = false;
    this.userMenuActive = false;
  }

  closeAllDropdowns(): void {
    this.closeHeaderDropdowns();
    this.notificationsMenu?.closeMenu();
  }

  openFeedbackModal(): void {
    this.feedbackService.openFeedbackModal();
  }
}
